/**
 * Which page buttons to show — pure, so the windowing is testable without a render.
 *
 * Two properties the tests pin, both learned from paginators that got them wrong:
 *
 * - **First and last page are always reachable.** A window that slides off page 1
 *   strands the user deep in a result set with no way home but arrow-mashing.
 * - **The slot count is CONSTANT for a given `siblingCount`** (7 slots at the
 *   default) once the page count exceeds it. A row that gains and loses buttons as
 *   the current page moves makes the Previous/Next targets jump under the pointer —
 *   the boundary window is widened to spend the slot the closed gap freed.
 */

export type PaginationItem = number | 'gap-start' | 'gap-end';

const span = (from: number, to: number): number[] =>
  Array.from({ length: to - from + 1 }, (_, i) => from + i);

export const paginationRange = (
  page: number,
  pageCount: number,
  siblingCount = 1,
): PaginationItem[] => {
  /* first + last + current + siblings each side + the two gaps */
  const slots = 2 * siblingCount + 5;
  if (pageCount <= slots) return span(1, Math.max(pageCount, 0));

  const current = Math.min(Math.max(page, 1), pageCount);
  const left = Math.max(current - siblingCount, 1);
  const right = Math.min(current + siblingCount, pageCount);
  const leftGap = left > 2;
  const rightGap = right < pageCount - 1;
  /* A closed gap frees two slots (the gap and the far page's neighbour); the
     window on that side widens by the same amount, keeping `slots` constant. */
  const window = 2 * siblingCount + 3;

  if (!leftGap && rightGap) return [...span(1, window), 'gap-end', pageCount];
  if (leftGap && !rightGap) return [1, 'gap-start', ...span(pageCount - window + 1, pageCount)];
  return [1, 'gap-start', ...span(left, right), 'gap-end', pageCount];
};
